import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import ShareCampaignModal from '../components/ShareCampaignModal';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Plus, 
  Send, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Share2, 
  Save, 
  MoreVertical,
  Trash2,
  Search,
  Filter,
  SortAsc,
  SortDesc,
  Calendar,
  RefreshCw,
  Loader2,
  Edit
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Campaigns = ({ embedded = false }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Filtering & Sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { token } = useAuth();

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/v2/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaigns(response.data.campaigns || response.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des campagnes');
    } finally {
      setLoading(false);
    }
  };

  // Filtered and sorted campaigns
  const filteredCampaigns = useMemo(() => {
    let result = [...campaigns];
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c => 
        c.name?.toLowerCase().includes(term) ||
        c.message_type?.toLowerCase().includes(term)
      );
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(c => c.status === statusFilter);
    }
    
    // Type filter
    if (typeFilter !== 'all') {
      result = result.filter(c => c.message_type === typeFilter);
    }
    
    // Sorting
    result.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      // Handle dates
      if (sortBy === 'created_at' || sortBy === 'scheduled_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      
      // Handle numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle strings
      aVal = String(aVal || '').toLowerCase();
      bVal = String(bVal || '').toLowerCase();
      
      if (sortOrder === 'asc') {
        return aVal.localeCompare(bVal);
      }
      return bVal.localeCompare(aVal);
    });
    
    return result;
  }, [campaigns, searchTerm, statusFilter, typeFilter, sortBy, sortOrder]);

  const handleShareClick = (e, campaign) => {
    e.stopPropagation();
    setSelectedCampaign(campaign);
    setShareModalOpen(true);
  };

  const handleSaveDraft = async (e, campaign) => {
    e.stopPropagation();
    try {
      await axios.post(`${API_URL}/v2/campaigns/${campaign.id}/save-draft`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Campagne sauvegardée comme brouillon');
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteClick = (e, campaign) => {
    e.stopPropagation();
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!campaignToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/v2/campaigns/${campaignToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Campagne supprimée');
      setCampaigns(prev => prev.filter(c => c.id !== campaignToDelete.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'active':
      case 'sending':
        return <Send className="w-5 h-5 text-blue-500" />;
      case 'scheduled':
        return <Clock className="w-5 h-5 text-amber-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-slate-400" />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'draft': 'Brouillon',
      'scheduled': 'Programmée',
      'active': 'En cours',
      'sending': 'Envoi...',
      'sent': 'Envoyée',
      'completed': 'Terminée',
      'failed': 'Échouée',
      'cancelled': 'Annulée'
    };
    return labels[status] || status;
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // Get unique values for filters
  const statusOptions = useMemo(() => {
    const statuses = [...new Set(campaigns.map(c => c.status).filter(Boolean))];
    return statuses;
  }, [campaigns]);

  const typeOptions = useMemo(() => {
    const types = [...new Set(campaigns.map(c => c.message_type).filter(Boolean))];
    return types;
  }, [campaigns]);

  const content = (
    <div className="space-y-6" data-testid="campaigns-list">
      {/* Header with filters */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Mes Campagnes
            </h2>
            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {filteredCampaigns.length} campagne(s) {searchTerm || statusFilter !== 'all' || typeFilter !== 'all' ? 'filtrée(s)' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchCampaigns}
              disabled={loading}
              className={isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              onClick={() => navigate('/campaigns/new')}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle campagne
            </Button>
          </div>
        </div>

        {/* Filters Bar */}
        <Card className={`${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                  <Input
                    placeholder="Rechercher une campagne..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`pl-10 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="w-[160px]">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}>
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    {statusOptions.map(status => (
                      <SelectItem key={status} value={status}>{getStatusLabel(status)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Type Filter */}
              <div className="w-[160px]">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}>
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    {typeOptions.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sort */}
              <div className="w-[180px]">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}>
                    <SelectValue placeholder="Trier par" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at">Date de création</SelectItem>
                    <SelectItem value="name">Nom</SelectItem>
                    <SelectItem value="status">Statut</SelectItem>
                    <SelectItem value="total_recipients">Destinataires</SelectItem>
                    <SelectItem value="sent_count">Envoyés</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Sort Order Toggle */}
              <Button
                variant="outline"
                size="icon"
                onClick={toggleSortOrder}
                className={isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}
                title={sortOrder === 'asc' ? 'Croissant' : 'Décroissant'}
              >
                {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
              </Button>

              {/* Clear Filters */}
              {(searchTerm || statusFilter !== 'all' || typeFilter !== 'all') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setTypeFilter('all');
                  }}
                  className="text-indigo-600"
                >
                  Réinitialiser
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaigns List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
          <CardContent className="py-12 text-center">
            <Send className={`h-12 w-12 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`} />
            <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {searchTerm || statusFilter !== 'all' || typeFilter !== 'all' 
                ? 'Aucune campagne trouvée'
                : 'Aucune campagne'}
            </h3>
            <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
              {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                ? 'Essayez de modifier vos filtres'
                : 'Créez votre première campagne RCS'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredCampaigns.map((campaign) => (
            <Card
              key={campaign.id}
              className={`rounded-2xl transition-all cursor-pointer ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)] hover:border-indigo-500/30' : 'border-slate-100 hover:border-indigo-100'}`}
              onClick={() => navigate(`/campaigns/${campaign.id}`)}
              data-testid={`campaign-card-${campaign.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(campaign.status)}
                      <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-[hsl(220,13%,85%)]' : 'text-slate-900'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {campaign.name}
                      </h3>
                      {campaign.shared_with?.length > 0 && (
                        <Badge className="bg-indigo-100 text-indigo-700">
                          <Share2 className="h-3 w-3 mr-1" />
                          Partagée
                        </Badge>
                      )}
                    </div>
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className={`text-xs ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>Destinataires</p>
                        <p className={`text-lg font-semibold ${isDarkMode ? 'text-[hsl(220,13%,80%)]' : 'text-slate-900'}`}>{campaign.total_recipients || 0}</p>
                      </div>
                      <div>
                        <p className={`text-xs ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>Envoyés</p>
                        <p className="text-lg font-semibold text-blue-500">{campaign.sent_count || 0}</p>
                      </div>
                      <div>
                        <p className={`text-xs ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>Livrés</p>
                        <p className="text-lg font-semibold text-emerald-500">{campaign.delivered_count || 0}</p>
                      </div>
                      <div>
                        <p className={`text-xs ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>Lus</p>
                        <p className="text-lg font-semibold text-indigo-500">{campaign.read_count || 0}</p>
                      </div>
                    </div>
                    <div className={`mt-4 flex items-center space-x-4 text-sm ${isDarkMode ? 'text-[hsl(220,12%,50%)]' : 'text-slate-500'}`}>
                      <span>Type: {campaign.message_type || 'N/A'}</span>
                      <span>•</span>
                      <span>Créé le {new Date(campaign.created_at).toLocaleDateString('fr-FR')}</span>
                      {campaign.scheduled_at && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Programmé pour le {new Date(campaign.scheduled_at).toLocaleDateString('fr-FR')}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`${
                      campaign.status === 'sent' || campaign.status === 'completed' 
                        ? (isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-green-50 text-green-700') 
                        : campaign.status === 'active' || campaign.status === 'sending'
                          ? (isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-700')
                          : campaign.status === 'failed'
                            ? (isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-50 text-red-700')
                            : (isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-yellow-50 text-yellow-700')
                    }`}>
                      {getStatusLabel(campaign.status)}
                    </Badge>
                    
                    {/* Actions Menu */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 w-8 p-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/send-rcs?edit=${campaign.id}`);
                          }}
                          className={`cursor-pointer ${isDarkMode ? 'text-slate-200 hover:bg-[hsl(220,14%,20%)]' : ''}`}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Éditer
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={(e) => handleShareClick(e, campaign)}
                          className={`cursor-pointer ${isDarkMode ? 'text-slate-200 hover:bg-[hsl(220,14%,20%)]' : ''}`}
                        >
                          <Share2 className="h-4 w-4 mr-2" />
                          Partager
                        </DropdownMenuItem>
                        {campaign.status !== 'sent' && campaign.status !== 'completed' && (
                          <DropdownMenuItem 
                            onClick={(e) => handleSaveDraft(e, campaign)}
                            className={`cursor-pointer ${isDarkMode ? 'text-slate-200 hover:bg-[hsl(220,14%,20%)]' : ''}`}
                          >
                            <Save className="h-4 w-4 mr-2" />
                            Sauvegarder
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator className={isDarkMode ? 'bg-[hsl(220,14%,25%)]' : ''} />
                        <DropdownMenuItem 
                          onClick={(e) => handleDeleteClick(e, campaign)}
                          className={`cursor-pointer text-red-600 ${isDarkMode ? 'hover:bg-red-500/10' : 'hover:bg-red-50'}`}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Share Campaign Modal */}
      <ShareCampaignModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        campaign={selectedCampaign}
        onShareSuccess={() => {
          fetchCampaigns();
          toast.success('Campagne partagée avec succès');
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
          <AlertDialogHeader>
            <AlertDialogTitle className={isDarkMode ? 'text-white' : ''}>
              Supprimer la campagne ?
            </AlertDialogTitle>
            <AlertDialogDescription className={isDarkMode ? 'text-slate-400' : ''}>
              Êtes-vous sûr de vouloir supprimer la campagne "{campaignToDelete?.name}" ? 
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={isDarkMode ? 'border-[hsl(220,14%,25%)] text-slate-300' : ''}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
  
  return embedded ? content : <Layout>{content}</Layout>;
};

export default Campaigns;
